import type { ReactNode } from "react"

import { ChevronsUpDown } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useI18n } from "@/i18n"

interface ProfileMenuProps {
  /** The profile card; it becomes the menu trigger. */
  children: ReactNode
  /** Whether the backend offers Work IQ at all; without it there is nothing to switch. */
  workIqAvailable: boolean
  workIq: boolean
  onWorkIqChange: (workIq: boolean) => void
}

/**
 * Profile settings in the sidebar's bottom-left corner. Holds the Work IQ / Classic mode:
 * Work IQ searches SharePoint, OneDrive, mail, Teams and meetings in one request with the
 * user's own permissions (uses Copilot credits); Classic is the SharePoint search with
 * per-request source scopes.
 */
export function ProfileMenu({ children, workIqAvailable, workIq, onWorkIqChange }: ProfileMenuProps) {
  const { t } = useI18n()
  const modeLabel = workIq ? t("sidebar.workIq") : t("sidebar.classic")
  if (!workIqAvailable) return <div className="rounded-lg border border-border-subtle bg-card p-2.5">{children}</div>
  return (
    <DropdownMenu>
      <div className="flex items-center gap-2 rounded-lg border border-border-subtle bg-card p-2.5">
        <div className="min-w-0 flex-1">{children}</div>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("sidebar.searchModeAria", { mode: modeLabel })}
            title={t("sidebar.searchModeTitle")}
            className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full border border-orange px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-orange hover:text-on-orange"
          >
            {modeLabel}
            <ChevronsUpDown className="size-3" />
          </button>
        </DropdownMenuTrigger>
      </div>
      <DropdownMenuContent side="top" align="end" className="w-64">
        <DropdownMenuLabel>{t("sidebar.searchMode")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={workIq ? "work-iq" : "classic"} onValueChange={(v) => onWorkIqChange(v === "work-iq")}>
          <DropdownMenuRadioItem value="work-iq" title={t("sidebar.workIqHint")}>
            {t("sidebar.workIq")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="classic" title={t("sidebar.classicHint")}>
            {t("sidebar.classic")}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
